/**
 * Onboarding Chatbot Knowledge Base
 *
 * Contains comprehensive information about the SEO Intelligence Platform
 * features, workflows, and common user questions.
 */

export interface HelpTopic {
  id: string;
  category: string;
  title: string;
  keywords: string[];
  content: string;
  relatedTopics?: string[];
}

export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  description: string;
  route?: string;
  topicId?: string;
}

// Quick actions for common user needs
export const quickActions: QuickAction[] = [
  {
    id: 'getting-started',
    label: 'Rozpocznij',
    icon: 'rocket',
    description: 'Dowiedz się jak zacząć korzystać z platformy',
    topicId: 'start-overview',
  },
  {
    id: 'company-profile',
    label: 'Profil firmy',
    icon: 'building',
    description: 'Skonfiguruj profil swojej firmy',
    route: '/app/company-profile',
    topicId: 'company-profile',
  },
  {
    id: 'llm-settings',
    label: 'Ustawienia AI',
    icon: 'brain',
    description: 'Skonfiguruj klucze API dla modeli AI',
    route: '/settings',
    topicId: 'llm-settings',
  },
  {
    id: 'integrations',
    label: 'Integracje',
    icon: 'plug',
    description: 'Połącz Google Search Console i Analytics',
    route: '/app/integrations',
    topicId: 'integrations-overview',
  },
  {
    id: 'keyword-research',
    label: 'Słowa kluczowe',
    icon: 'search',
    description: 'Analizuj i odkrywaj słowa kluczowe',
    route: '/keyword-research',
    topicId: 'keyword-research',
  },
  {
    id: 'ai-visibility',
    label: 'AI Visibility',
    icon: 'eye',
    description: 'Sprawdź widoczność w wyszukiwarkach AI',
    route: '/ai-visibility',
    topicId: 'ai-visibility',
  },
];

// Comprehensive knowledge base topics
export const helpTopics: HelpTopic[] = [
  // === GETTING STARTED ===
  {
    id: 'start-overview',
    category: 'Rozpoczęcie',
    title: 'Jak zacząć korzystać z platformy?',
    keywords: ['start', 'początek', 'rozpoczęcie', 'pierwsze kroki', 'jak zacząć', 'nowy użytkownik'],
    content: `Witaj na platformie SEO Intelligence! Oto 5 kroków, aby rozpocząć:

**1. Profil Firmy** (Wymagane)
Przejdź do sekcji "Profil Firmy" i uzupełnij podstawowe dane:
- Domena Twojej strony
- Branża i opis działalności
- Główni konkurenci

**2. Ustawienia AI** (Wymagane)
W zakładce "Ustawienia" dodaj swój klucz API:
- OpenAI (GPT-4) - najpopularniejszy wybór
- Anthropic (Claude) - świetny do analiz
- Google (Gemini) - dobry stosunek ceny do jakości

**3. Integracje Google** (Zalecane)
Połącz swoje konta Google:
- Search Console - dane o pozycjach i kliknięciach
- Analytics 4 - statystyki ruchu
- PageSpeed Insights - wydajność strony

**4. Pierwsza Analiza**
Wypróbuj narzędzia:
- Analiza słów kluczowych
- Audyt techniczny SEO
- AI Visibility (GEO)

**5. Eksploruj Dashboard**
Sprawdź główny panel, gdzie znajdziesz podsumowanie wszystkich danych SEO.`,
    relatedTopics: ['company-profile', 'llm-settings', 'integrations-overview'],
  },

  // === COMPANY PROFILE ===
  {
    id: 'company-profile',
    category: 'Profil Firmy',
    title: 'Jak skonfigurować profil firmy?',
    keywords: ['profil', 'firma', 'domena', 'branża', 'konfiguracja', 'ustawienia firmy'],
    content: `Profil Firmy to serce platformy - wszystkie analizy bazują na tych danych.

**Podstawowe informacje:**
- **Domena** - adres Twojej strony (np. example.com)
- **Branża** - wybierz z listy lub wpisz własną
- **Opis firmy** - krótki opis działalności
- **Grupa docelowa** - kto jest Twoim klientem

**Dane konkurencji:**
- Dodaj 3-5 głównych konkurentów
- System automatycznie będzie ich monitorować

**Słowa kluczowe:**
- Dodaj główne frazy, na których Ci zależy
- System będzie śledzić ich pozycje

**Wskazówka:** Im dokładniej wypełnisz profil, tym trafniejsze będą rekomendacje AI.

Przejdź do: Profil Firmy → Edytuj → Zapisz zmiany`,
    relatedTopics: ['start-overview', 'keyword-research'],
  },

  // === LLM SETTINGS ===
  {
    id: 'llm-settings',
    category: 'Ustawienia AI',
    title: 'Jak skonfigurować modele AI?',
    keywords: ['ai', 'llm', 'openai', 'gpt', 'claude', 'gemini', 'api', 'klucz', 'ustawienia'],
    content: `Platforma wykorzystuje modele AI do analiz SEO. Musisz dostarczyć własny klucz API (BYOK - Bring Your Own Key).

**Dostępni dostawcy:**

**OpenAI (GPT-4)**
- Najlepszy do ogólnych analiz SEO
- Koszt: ~$0.03/1K tokenów
- Gdzie uzyskać: platform.openai.com

**Anthropic (Claude)**
- Świetny do długich analiz treści
- Koszt: ~$0.015/1K tokenów
- Gdzie uzyskać: console.anthropic.com

**Google (Gemini)**
- Dobry stosunek ceny do jakości
- Koszt: ~$0.001/1K tokenów
- Gdzie uzyskać: aistudio.google.com

**Jak dodać klucz:**
1. Przejdź do Ustawienia → Modele AI
2. Wybierz dostawcę
3. Wklej klucz API
4. Wybierz modele dla różnych zadań
5. Zapisz

**Wskazówka:** Zacznij od GPT-4o-mini - to najtańszy i najszybszy model dla większości zadań.`,
    relatedTopics: ['start-overview', 'costs'],
  },

  {
    id: 'costs',
    category: 'Ustawienia AI',
    title: 'Ile kosztuje korzystanie z AI?',
    keywords: ['koszt', 'cena', 'ile kosztuje', 'płatność', 'tokeny', 'budżet'],
    content: `Koszty AI zależą od ilości analiz i wybranego modelu.

**Szacunkowe koszty miesięczne:**

**Lekkie użycie** (10-20 analiz)
- GPT-4o-mini: ~$0.50-1.00
- Gemini Flash: ~$0.10-0.20

**Średnie użycie** (50-100 analiz)
- GPT-4o-mini: ~$2-5
- GPT-4o: ~$10-20

**Intensywne użycie** (200+ analiz)
- GPT-4o-mini: ~$5-10
- GPT-4o: ~$30-50

**Wskazówki oszczędzania:**
- Używaj GPT-4o-mini do prostych zadań
- GPT-4o tylko do złożonych analiz
- Gemini Flash do testów

**Śledzenie kosztów:**
W panelu Ustawienia znajdziesz statystyki użycia tokenów.`,
    relatedTopics: ['llm-settings'],
  },

  // === INTEGRATIONS ===
  {
    id: 'integrations-overview',
    category: 'Integracje',
    title: 'Jakie integracje są dostępne?',
    keywords: ['integracje', 'google', 'połączenie', 'oauth', 'search console', 'analytics'],
    content: `Platforma integruje się z narzędziami Google, aby pobierać dane o Twojej stronie.

**Dostępne integracje:**

**Google Search Console**
- Pozycje w wynikach wyszukiwania
- Kliknięcia i wyświetlenia
- CTR i średnia pozycja
- Problemy z indeksowaniem

**Google Analytics 4**
- Ruch na stronie
- Źródła odwiedzin
- Zachowanie użytkowników
- Konwersje

**PageSpeed Insights**
- Core Web Vitals (LCP, FID, CLS)
- Wydajność mobilna i desktop
- Rekomendacje optymalizacji

**Google Ads** (opcjonalnie)
- Dane o kampaniach
- Koszt słów kluczowych
- Konwersje z reklam

**Jak połączyć:**
1. Przejdź do Integracje
2. Kliknij "Połącz" przy wybranej usłudze
3. Zaloguj się kontem Google
4. Wybierz odpowiednią usługę (property)
5. Zatwierdź uprawnienia

Dane synchronizują się automatycznie co 24 godziny.`,
    relatedTopics: ['gsc-integration', 'ga4-integration', 'pagespeed-integration'],
  },

  {
    id: 'gsc-integration',
    category: 'Integracje',
    title: 'Jak połączyć Google Search Console?',
    keywords: ['search console', 'gsc', 'google', 'pozycje', 'kliknięcia'],
    content: `Google Search Console dostarcza dane o widoczności w wyszukiwarce Google.

**Wymagania:**
- Konto Google z dostępem do GSC
- Zweryfikowana domena w GSC

**Krok po kroku:**
1. Przejdź do Integracje → Google Search Console
2. Kliknij "Połącz konto"
3. Wybierz konto Google
4. Wybierz property (domenę)
5. Zatwierdź uprawnienia

**Dostępne dane:**
- Top 100 zapytań (queries)
- Najlepsze strony (pages)
- Kliknięcia, wyświetlenia, CTR
- Średnia pozycja
- Trendy czasowe

**Wskazówka:** Wybierz property domenową (Domain property) zamiast URL prefix, aby mieć pełne dane.

Jeśli nie widzisz swojej domeny, upewnij się że masz uprawnienia "Właściciel" lub "Pełny użytkownik" w GSC.`,
    relatedTopics: ['integrations-overview', 'analytics-data'],
  },

  {
    id: 'ga4-integration',
    category: 'Integracje',
    title: 'Jak połączyć Google Analytics 4?',
    keywords: ['analytics', 'ga4', 'ruch', 'statystyki', 'odwiedziny'],
    content: `Google Analytics 4 dostarcza dane o ruchu i zachowaniu użytkowników.

**Wymagania:**
- Konto Google z dostępem do GA4
- Skonfigurowana usługa GA4

**Krok po kroku:**
1. Przejdź do Integracje → Google Analytics 4
2. Kliknij "Połącz konto"
3. Wybierz konto Google
4. Wybierz property GA4
5. Zatwierdź uprawnienia

**Dostępne dane:**
- Sesje i użytkownicy
- Źródła ruchu (organic, direct, social)
- Współczynnik odrzuceń
- Czas na stronie
- Najczęściej odwiedzane strony

**Wskazówka:** Upewnij się, że masz zainstalowany kod śledzenia GA4 na stronie i zbiera on dane od minimum 30 dni.`,
    relatedTopics: ['integrations-overview', 'analytics-data'],
  },

  {
    id: 'pagespeed-integration',
    category: 'Integracje',
    title: 'Jak sprawdzić PageSpeed Insights?',
    keywords: ['pagespeed', 'wydajność', 'szybkość', 'core web vitals', 'lcp', 'cls'],
    content: `PageSpeed Insights analizuje wydajność Twojej strony.

**Dostępne metryki:**

**Core Web Vitals:**
- **LCP** (Largest Contentful Paint) - czas ładowania głównej treści
  - Dobry: < 2.5s
  - Wymaga poprawy: 2.5-4s
  - Słaby: > 4s

- **FID** (First Input Delay) - czas reakcji na interakcję
  - Dobry: < 100ms
  - Wymaga poprawy: 100-300ms
  - Słaby: > 300ms

- **CLS** (Cumulative Layout Shift) - stabilność wizualna
  - Dobry: < 0.1
  - Wymaga poprawy: 0.1-0.25
  - Słaby: > 0.25

**Jak uruchomić audyt:**
1. Przejdź do PageSpeed
2. Wpisz URL strony
3. Kliknij "Analizuj"
4. Poczekaj na wyniki (30-60 sekund)

**Wskazówka:** Sprawdź zarówno wersję mobilną jak i desktop - Google priorytetyzuje wyniki mobilne.`,
    relatedTopics: ['integrations-overview', 'technical-seo'],
  },

  // === KEYWORD RESEARCH ===
  {
    id: 'keyword-research',
    category: 'Analiza słów kluczowych',
    title: 'Jak analizować słowa kluczowe?',
    keywords: ['słowa kluczowe', 'keywords', 'frazy', 'analiza', 'wolumen', 'trudność'],
    content: `Moduł Keyword Research pomaga znaleźć i analizować słowa kluczowe.

**Dostępne funkcje:**

**Analiza słowa kluczowego**
- Wolumen wyszukiwań (miesięczny)
- Trudność (Keyword Difficulty)
- CPC (koszt kliknięcia w reklamach)
- Trend wyszukiwań
- Powiązane słowa kluczowe

**Sugestie słów kluczowych**
- Long-tail keywords
- Pytania użytkowników
- Powiązane tematy

**Analiza konkurencji**
- Kto rankuje na dane słowo
- SERP features (snippets, images, etc.)

**Jak korzystać:**
1. Przejdź do Keyword Research
2. Wpisz słowo kluczowe
3. Opcjonalnie: ustaw lokalizację i język
4. Kliknij "Analizuj"

**Wskazówka:** Szukaj słów z:
- Wolumen: 100-10,000/mies
- Trudność: < 50
- Intencja: transakcyjna lub informacyjna`,
    relatedTopics: ['serp-tracker', 'content-writer'],
  },

  // === COMPETITOR ANALYSIS ===
  {
    id: 'competitor-analysis',
    category: 'Analiza konkurencji',
    title: 'Jak analizować konkurencję?',
    keywords: ['konkurencja', 'konkurenci', 'analiza', 'porównanie', 'benchmarking'],
    content: `Moduł Competitor Analysis pokazuje, jak wypadasz na tle konkurencji.

**Dostępne analizy:**

**Profil konkurenta**
- Domain Authority
- Liczba słów kluczowych
- Szacowany ruch organiczny
- Profil linków

**Porównanie słów kluczowych**
- Wspólne słowa kluczowe
- Unikalne słowa konkurenta
- Content gap (luki w treści)

**Analiza linków**
- Źródła linków konkurenta
- Jakość profilu linkowego
- Możliwości zdobycia linków

**Jak korzystać:**
1. Przejdź do Competitor Analysis
2. Dodaj domeny konkurentów (do 5)
3. Wybierz typ analizy
4. Kliknij "Analizuj"

**Wskazówka:** Dodaj konkurentów do profilu firmy, aby automatycznie śledzić ich działania.`,
    relatedTopics: ['company-profile', 'backlink-analysis'],
  },

  // === BACKLINK ANALYSIS ===
  {
    id: 'backlink-analysis',
    category: 'Analiza linków',
    title: 'Jak analizować linki zwrotne?',
    keywords: ['linki', 'backlinks', 'link building', 'profil linkowy', 'referring domains'],
    content: `Moduł Backlink Analysis analizuje profil linków Twojej strony.

**Dostępne metryki:**

**Profil linkowy**
- Liczba backlinków
- Referring domains (unikalne domeny)
- Domain Rating / Authority
- Dofollow vs nofollow

**Jakość linków**
- Toksyczne linki (do usunięcia)
- Wartościowe linki
- Anchor text distribution

**Możliwości**
- Broken link opportunities
- Competitor backlinks (do zdobycia)
- Guest posting opportunities

**Jak korzystać:**
1. Przejdź do Backlink Analysis
2. Domena z profilu załaduje się automatycznie
3. Wybierz typ analizy
4. Kliknij "Analizuj"

**Wskazówka:** Regularnie sprawdzaj toksyczne linki i zgłaszaj je do Google Disavow Tool.`,
    relatedTopics: ['competitor-analysis', 'technical-seo'],
  },

  // === CONTENT WRITER ===
  {
    id: 'content-writer',
    category: 'Tworzenie treści',
    title: 'Jak tworzyć treści SEO?',
    keywords: ['treść', 'content', 'artykuł', 'pisanie', 'seo copywriting'],
    content: `Content Writer to asystent AI do tworzenia treści zoptymalizowanych pod SEO.

**Możliwości:**

**Generowanie treści**
- Artykuły blogowe
- Opisy produktów
- Meta tagi (title, description)
- Nagłówki H1-H6

**Optymalizacja istniejącej treści**
- Analiza SEO treści
- Sugestie poprawek
- Dodawanie słów kluczowych

**Personalizacja**
- Ton komunikacji (formalny/nieformalny)
- Długość tekstu
- Język i styl

**Jak korzystać:**
1. Przejdź do Content Writer
2. Wybierz typ treści
3. Podaj temat i słowa kluczowe
4. Ustaw parametry (długość, ton)
5. Kliknij "Generuj"

**Wskazówka:** Zawsze edytuj wygenerowany tekst - AI tworzy szkic, nie finalną wersję!`,
    relatedTopics: ['keyword-research', 'ai-visibility'],
  },

  // === TECHNICAL SEO ===
  {
    id: 'technical-seo',
    category: 'SEO Techniczne',
    title: 'Jak przeprowadzić audyt techniczny?',
    keywords: ['techniczne', 'audyt', 'błędy', 'crawl', 'indeksowanie'],
    content: `Moduł Technical SEO przeprowadza kompleksowy audyt techniczny strony.

**Sprawdzane elementy:**

**Crawlability**
- Robots.txt
- Sitemap.xml
- Canonical tags
- Przekierowania (301, 302)

**Mobile-friendliness**
- Responsywność
- Viewport settings
- Touch elements

**Wydajność**
- Core Web Vitals
- Czas ładowania
- Rozmiar strony

**Bezpieczeństwo**
- HTTPS
- Mixed content
- Security headers

**Jak przeprowadzić audyt:**
1. Przejdź do Technical SEO
2. Domena załaduje się z profilu
3. Kliknij "Uruchom audyt"
4. Poczekaj na wyniki (2-5 minut)

**Wskazówka:** Napraw najpierw błędy krytyczne (czerwone), potem ostrzeżenia (żółte).`,
    relatedTopics: ['pagespeed-integration', 'start-overview'],
  },

  // === SERP TRACKER ===
  {
    id: 'serp-tracker',
    category: 'Śledzenie pozycji',
    title: 'Jak śledzić pozycje w Google?',
    keywords: ['pozycje', 'ranking', 'serp', 'monitorowanie', 'tracking'],
    content: `SERP Tracker monitoruje pozycje Twoich słów kluczowych w wynikach wyszukiwania.

**Funkcje:**

**Śledzenie pozycji**
- Aktualna pozycja (1-100)
- Zmiana pozycji (▲ ▼)
- Historia pozycji (wykres)
- Porównanie z konkurencją

**Alerty**
- Powiadomienia o zmianach
- Spadki pozycji
- Nowe TOP 10

**Raporty**
- Średnia pozycja
- Widoczność w SERP
- Trendy czasowe

**Jak dodać słowa do śledzenia:**
1. Przejdź do SERP Tracker
2. Kliknij "Dodaj słowa kluczowe"
3. Wpisz frazy (jedno na linię)
4. Wybierz lokalizację i urządzenie
5. Zapisz

**Wskazówka:** Śledź 20-50 najważniejszych słów kluczowych, nie setki - to daje lepszy focus.`,
    relatedTopics: ['keyword-research', 'analytics-data'],
  },

  // === AI VISIBILITY (GEO) ===
  {
    id: 'ai-visibility',
    category: 'AI Visibility',
    title: 'Czym jest AI Visibility / GEO?',
    keywords: ['ai visibility', 'geo', 'chatgpt', 'perplexity', 'ai search', 'generative'],
    content: `AI Visibility (Generative Engine Optimization) to nowa dziedzina SEO dla wyszukiwarek AI.

**Dlaczego to ważne?**
Coraz więcej osób szuka informacji przez:
- ChatGPT
- Perplexity
- Google SGE (AI Overview)
- Microsoft Copilot

**Co mierzymy:**

**Widoczność w AI**
- Czy Twoja marka jest cytowana?
- W jakim kontekście?
- Jak często?

**Analiza odpowiedzi AI**
- Sentiment (pozytywny/neutralny/negatywny)
- Dokładność informacji
- Porównanie z konkurencją

**Rekomendacje**
- Jak poprawić widoczność
- Jakie treści tworzyć
- Gdzie się promować

**Jak sprawdzić:**
1. Przejdź do AI Visibility
2. Wpisz nazwę marki lub domenę
3. Wybierz model AI do sprawdzenia
4. Kliknij "Analizuj"

**Wskazówka:** Twórz treści eksperckie, z danymi i źródłami - AI preferuje wiarygodne źródła.`,
    relatedTopics: ['content-writer', 'keyword-research'],
  },

  // === ANALYTICS DATA ===
  {
    id: 'analytics-data',
    category: 'Analityka',
    title: 'Jak interpretować dane analityczne?',
    keywords: ['analityka', 'dane', 'raporty', 'metryki', 'kpi'],
    content: `Dashboard zbiera dane z różnych źródeł w jedno miejsce.

**Główne metryki:**

**Ruch organiczny**
- Sesje z wyszukiwarek
- Użytkownicy organiczni
- Trend wzrostu/spadku

**Widoczność w wyszukiwarkach**
- Wyświetlenia w SERP
- Kliknięcia
- CTR (Click-Through Rate)
- Średnia pozycja

**Wydajność strony**
- Core Web Vitals score
- PageSpeed score
- Mobile usability

**Profil linkowy**
- Liczba backlinków
- Referring domains
- Domain Authority

**Jak czytać Dashboard:**
1. Zielone wartości = wzrost / dobre
2. Czerwone wartości = spadek / wymaga uwagi
3. Strzałki ▲ ▼ pokazują trend
4. Kliknij w metrykę, aby zobaczyć szczegóły

**Wskazówka:** Sprawdzaj Dashboard co tydzień, aby wychwycić trendy i anomalie.`,
    relatedTopics: ['start-overview', 'gsc-integration', 'ga4-integration'],
  },

  // === TROUBLESHOOTING ===
  {
    id: 'troubleshooting',
    category: 'Rozwiązywanie problemów',
    title: 'Coś nie działa - co robić?',
    keywords: ['problem', 'błąd', 'nie działa', 'pomoc', 'error', 'awaria'],
    content: `Najczęstsze problemy i ich rozwiązania:

**"Brak danych z GSC/GA4"**
- Sprawdź czy integracja jest aktywna (Integracje)
- Odśwież połączenie
- Upewnij się, że masz odpowiednie uprawnienia

**"Błąd API AI"**
- Sprawdź poprawność klucza API (Ustawienia)
- Sprawdź saldo na koncie dostawcy AI
- Spróbuj inny model

**"Analiza trwa zbyt długo"**
- Normalne dla dużych analiz (do 5 minut)
- Sprawdź połączenie internetowe
- Odśwież stronę i spróbuj ponownie

**"Brak wyników dla domeny"**
- Upewnij się, że domena jest poprawna
- Sprawdź czy strona jest zaindeksowana
- Poczekaj 24h po pierwszej konfiguracji

**"Nie mogę się zalogować"**
- Zresetuj hasło
- Wyczyść cache przeglądarki
- Spróbuj inną przeglądarkę

**Nadal masz problem?**
Napisz na: support@seo-platform.pl`,
    relatedTopics: ['start-overview', 'llm-settings'],
  },
];

// Function to search knowledge base
export function searchKnowledgeBase(query: string): HelpTopic[] {
  const normalizedQuery = query.toLowerCase().trim();
  const words = normalizedQuery.split(/\s+/);

  return helpTopics
    .map((topic) => {
      let score = 0;

      // Check title match
      if (topic.title.toLowerCase().includes(normalizedQuery)) {
        score += 10;
      }

      // Check keyword matches
      topic.keywords.forEach((keyword) => {
        if (keyword.toLowerCase().includes(normalizedQuery)) {
          score += 5;
        }
        words.forEach((word) => {
          if (word.length > 2 && keyword.toLowerCase().includes(word)) {
            score += 2;
          }
        });
      });

      // Check content match
      words.forEach((word) => {
        if (word.length > 3 && topic.content.toLowerCase().includes(word)) {
          score += 1;
        }
      });

      return { topic, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((item) => item.topic)
    .slice(0, 5);
}

// Function to get topic by ID
export function getTopicById(id: string): HelpTopic | undefined {
  return helpTopics.find((topic) => topic.id === id);
}

// Function to get related topics
export function getRelatedTopics(topic: HelpTopic): HelpTopic[] {
  if (!topic.relatedTopics) return [];
  return topic.relatedTopics
    .map((id) => getTopicById(id))
    .filter((t): t is HelpTopic => t !== undefined);
}

// Function to get topics by category
export function getTopicsByCategory(category: string): HelpTopic[] {
  return helpTopics.filter((topic) => topic.category === category);
}

// Get all unique categories
export function getCategories(): string[] {
  return [...new Set(helpTopics.map((topic) => topic.category))];
}
